import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectExtensionInfo } from '@/store/selectors';
import { versionUtils } from '@/utils';
import { IExtensionInfo } from '@/models';
import { ExtensionInstallRedirect } from '@/features/extension/ExtensionInstallRedirect/ExtensionInstallRedirect';

export enum EGuardType {
  extensionExists = 'extensionExists',
  extensionMinVersion = 'extensionMinVersion',
}

const GUARD_RESULT = {
  ALLOW: 'allow',
  INSTALL_EXTENSION: 'installExtension',
  LEGACY_ROUTE: 'legacyRoute',
  UNKNOWN: 'unknown',
} as const;
type GuardResult = (typeof GUARD_RESULT)[keyof typeof GUARD_RESULT];

interface IAppGuardProps {
  children: React.ReactNode;
  guardTypes: EGuardType[];
  legacyRoute: string;
}

// null means the install check never ran, for example when app init failed before it
const isInstallStateUnknown = (extensionInfo: IExtensionInfo): boolean =>
  extensionInfo.isInstalled === null;

const isExtensionMissing = (extensionInfo: IExtensionInfo): boolean =>
  !extensionInfo.isInstalled;

const isExtensionVersionUnknown = (extensionInfo: IExtensionInfo): boolean =>
  !extensionInfo.extensionVersion;

const isExtensionOutdated = (extensionInfo: IExtensionInfo): boolean =>
  !versionUtils.isAtLeast(
    extensionInfo.extensionVersion,
    versionUtils.extensionMinVersion
  );

const evaluateGuard = (
  type: EGuardType,
  extensionInfo: IExtensionInfo
): GuardResult => {
  if (isInstallStateUnknown(extensionInfo)) return GUARD_RESULT.UNKNOWN;
  if (isExtensionMissing(extensionInfo)) return GUARD_RESULT.INSTALL_EXTENSION;
  if (type === EGuardType.extensionExists) return GUARD_RESULT.ALLOW;

  if (isExtensionVersionUnknown(extensionInfo)) {
    return GUARD_RESULT.INSTALL_EXTENSION;
  }
  if (isExtensionOutdated(extensionInfo)) return GUARD_RESULT.LEGACY_ROUTE;
  return GUARD_RESULT.ALLOW;
};

const resolveGuards = (
  guardTypes: EGuardType[],
  extensionInfo: IExtensionInfo
): GuardResult => {
  for (const type of guardTypes) {
    const result = evaluateGuard(type, extensionInfo);
    if (result !== GUARD_RESULT.ALLOW) return result;
  }
  return GUARD_RESULT.ALLOW;
};

export const AppGuard: React.FC<IAppGuardProps> = (props: IAppGuardProps) => {
  const extensionInfo = useSelector(selectExtensionInfo);
  const guardResult = resolveGuards(props.guardTypes, extensionInfo);

  useEffect(() => {
    if (guardResult === GUARD_RESULT.LEGACY_ROUTE) {
      window.location.href = props.legacyRoute;
    }
  }, [guardResult, props.legacyRoute]);

  if (guardResult === GUARD_RESULT.INSTALL_EXTENSION) {
    return <ExtensionInstallRedirect />;
  }
  if (guardResult === GUARD_RESULT.LEGACY_ROUTE) return null;
  // no loader here, it would cover the init error popup with the reload button
  if (guardResult === GUARD_RESULT.UNKNOWN) return null;

  return <>{props.children}</>;
};
